import { ChangeDetectionStrategy, Component, Inject, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { API_MODE, TenantService } from '@vsolv/core/multi-tenant/web';
import { API_BASE_URL } from '@vsolv/dev-kit/ngx';
import { FIREBASE_AUTH, idToken$ } from '@vsolv/packages/firebase/web';
import { SecurityService } from '@wsphere/staff/web';
import { Auth } from 'firebase/auth';
import { BehaviorSubject, combineLatest, map, Observable, shareReplay, switchMap } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-reporting-page',
  templateUrl: './reporting.page.html',
  standalone: false,
})
export class ReportingPage {
  constructor(
    public readonly router: Router,
    public readonly route: ActivatedRoute,
    private tenantSvc: TenantService,
    @Inject(FIREBASE_AUTH) private auth: Auth,
    private securitySvc: SecurityService,
    @Inject(API_MODE) private apiMode: string
  ) {}

  private apiBaseUrl = inject(API_BASE_URL);
  private timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  startDate$ = new BehaviorSubject<Date | null>(null);
  endDate$ = new BehaviorSubject<Date | null>(null);
  productionStartDate$ = new BehaviorSubject<Date | null>(null);
  productionEndDate$ = new BehaviorSubject<Date | null>(null);
  distributorListStartDate$ = new BehaviorSubject<Date | null>(null);
  distributorListEndDate$ = new BehaviorSubject<Date | null>(null);
  customerListStartDate$ = new BehaviorSubject<Date | null>(null);
  customerListEndDate$ = new BehaviorSubject<Date | null>(null);

  canViewReports$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(globalDistributors => {
      const permissionKeys = globalDistributors?.map(dist => dist.permissionKey) ?? null;
      return this.securitySvc.hasAccess('rep_ViewReports', permissionKeys);
    })
  );

  canGenerateReports$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(globalDistributors => {
      const permissionKeys = globalDistributors?.map(dist => dist.permissionKey) ?? null;
      return this.securitySvc.hasAccess('rep_GenerateReports', permissionKeys);
    })
  );

  canViewBfsFinancing$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(globalDistributors => {
      const permissionKeys = globalDistributors?.map(dist => dist.permissionKey) ?? null;
      return this.securitySvc.hasAccess('bfs_FinanceAdmin', permissionKeys);
    })
  );

  protected exportUrl$ = combineLatest([
    this.canGenerateReports$,
    idToken$(this.auth),
    this.startDate$,
    this.endDate$,
  ]).pipe(
    map(([canGenerateReports, idToken, startDate, endDate]) => {
      if (!canGenerateReports) return null;

      const params = [
        `tenantId=${this.tenantSvc.tenantId}`,
        `mode=${this.apiMode}`,
        `idToken=${idToken}`,
        `from=${startDate}`,
        `to=${endDate}`,
      ].join('&');

      return `${this.apiBaseUrl}/v1/admin-dashboard/reporting/remittance-report?${params}`;
    })
  );

  protected productionExportUrl$ = combineLatest([
    this.canGenerateReports$,
    idToken$(this.auth),
    this.productionStartDate$,
    this.productionEndDate$,
  ]).pipe(
    map(([canGenerateReports, idToken, startDate, endDate]) => {
      if (!canGenerateReports || !startDate || !endDate) return null;

      const params = [
        `tenantId=${this.tenantSvc.tenantId}`,
        `mode=${this.apiMode}`,
        `idToken=${idToken}`,
        `startDate=${startDate.toISOString()}`,
        `endDate=${endDate.toISOString()}`,
        `timezone=${encodeURIComponent(this.timezone)}`,
      ].join('&');

      return `${this.apiBaseUrl}/v1/admin-dashboard/reporting/production-report?${params}`;
    })
  );

  protected distributorListExportUrl$ = this.contactListExportUrl(
    'distributor-list',
    this.distributorListStartDate$,
    this.distributorListEndDate$
  );

  protected customerListExportUrl$ = this.contactListExportUrl(
    'customer-list',
    this.customerListStartDate$,
    this.customerListEndDate$
  );

  private contactListExportUrl(
    report: 'distributor-list' | 'customer-list',
    startDate$: Observable<Date | null>,
    endDate$: Observable<Date | null>
  ) {
    return combineLatest([this.canGenerateReports$, idToken$(this.auth), startDate$, endDate$]).pipe(
      map(([canGenerateReports, idToken, startDate, endDate]) => {
        if (!canGenerateReports) return null;
        if (startDate && endDate && endDate < startDate) return null;

        const params = [
          `tenantId=${this.tenantSvc.tenantId}`,
          `mode=${this.apiMode}`,
          `idToken=${idToken}`,
          ...(startDate ? [`startDate=${startDate.toISOString()}`] : []),
          ...(endDate ? [`endDate=${endDate.toISOString()}`] : []),
          `timezone=${encodeURIComponent(this.timezone)}`,
        ].join('&');

        return `${this.apiBaseUrl}/v1/admin-dashboard/reporting/${report}?${params}`;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
}
